export const UserProfileAttribute = Object.freeze({
    EnableContributions: 'EnableContributions',
    EnableGithubLink: 'EnableGithubLink',
    EnableGitlabLink: 'EnableGitlabLink',
    EnableOriginalXprtBadge: 'EnableOriginalXprtBadge'
});

export const UserProfileRole = Object.freeze({
    Xprt: 'Xprt'
});

export const accessModeOptions = Object.freeze({
    Public: Object.freeze({
        icon: 'sym_r_public',
        color: 'positive',
        label: 'Public',
        description: 'Public access makes your profile visible to everyone, including those not logged in to Xprt. It\'s intended for maximum visibility, allowing any internet '
            + 'user to view your professional information.',
        requiresLinkedRepoAccount: true
    }),
    Restricted: Object.freeze({
        icon: 'sym_r_lock',
        color: 'negative',
        label: 'Restricted',
        description: 'Restricted access allows your profile to be viewed only through protected profile links. This setting is intended for selectively sharing professional '
            + 'details with a specific audience.',
        requiresLinkedRepoAccount: false
    })
});

// Special boolean indicates that the network is only permitted by linking their account via auth, whereas the rest can
//   simply be listed by entering their alias/username.
export const linkedProfileOptions = {
    'dev': {
        icon: 'fab fa-dev',
        label: 'DEV',
        color: '#000000',
        urlPrefix: 'dev.to/',
        permittedRoles: [ UserProfileRole.Xprt ]
    },
    'hashnode': {
        icon: 'fab fa-hashnode',
        label: 'Hashnode',
        color: '#415ef9',
        urlPrefix: 'hashnode.com/@',
        permittedRoles: [ UserProfileRole.Xprt ]
    },
    'github': {
        icon: 'fab fa-github',
        label: 'GitHub',
        color: '#333333',
        urlPrefix: 'github.com/',
        special: true,
        permittedRoles: [ UserProfileRole.Xprt ]
    },
    'gitlab': {
        icon: 'fab fa-gitlab',
        label: 'GitLab',
        color: '#fc6d26',
        urlPrefix: 'gitlab.com/',
        special: true,
        permittedRoles: [ UserProfileRole.Xprt ]
    },
    'linkedin': {
        icon: 'fab fa-linkedin',
        label: 'LinkedIn',
        color: '#0077b5',
        urlPrefix: 'linkedin.com/in/',
        permittedRoles: [ UserProfileRole.Xprt ]
    },
    'medium': {
        icon: 'fab fa-medium',
        label: 'Medium',
        color: '#000000',
        urlPrefix: 'medium.com/@',
        permittedRoles: [ UserProfileRole.Xprt ]
    },
    'reddit': {
        icon: 'fab fa-reddit',
        label: 'Reddit',
        color: '#ff4500',
        urlPrefix: 'reddit.com/user/',
        permittedRoles: [ UserProfileRole.Xprt ]
    },
    'stack-overflow': {
        icon: 'fab fa-stack-overflow',
        label: 'Stack Overflow',
        color: '#f47f24',
        urlPrefix: 'stackoverflow.com/users/',
        placeholder: 'id/username',
        permittedRoles: [ UserProfileRole.Xprt ]
    },
    'x': {
        icon: 'fab fa-x-twitter',
        label: 'X',
        color: '#000000',
        urlPrefix: 'x.com/',
        permittedRoles: [ UserProfileRole.Xprt ]
    },
    'y-combinator': {
        icon: 'fab fa-y-combinator',
        label: 'Y Combinator',
        color: '#ff4000',
        urlPrefix: 'news.ycombinator.com/user?id=',
        permittedRoles: [ UserProfileRole.Xprt ]
    },
    'youtube': {
        icon: 'fab fa-youtube',
        label: 'YouTube',
        color: '#ff0000',
        urlPrefix: 'youtube.com/@',
        placeholder: 'channel',
        permittedRoles: [ UserProfileRole.Xprt ]
    }
};

const sectionSortKeys = new Map(Object.entries({
    contributions: [
        [ 'expirationAt', 'desc', Date ],
        [ 'issuedAt', 'desc', Date ]
    ],
    projects: [
        [ 'endedAt', 'desc', Date ],
        [ 'startedAt', 'desc', Date ]
    ],
    repositories: [
        [ 'repository', 'asc' ]
    ],
    schools: [
        [ 'endedAt', 'desc', Date ],
        [ 'startedAt', 'desc', Date ]
    ],
    workplaces: [
        [ 'endedAt', 'desc', Date ],
        [ 'startedAt', 'desc', Date ]
    ]
}));

const MONTHS_IN_YEAR = 12;

/**
 * Reads the headline figures off a career profile: the span of experience, the roles held, the certifications and the
 * distinct skills tagged across every section.
 *
 * @param {Object} profile - The career profile.
 * @param {Date} [now] - The moment to measure open-ended spans against.
 * @returns {Object[]} - The figures, each with a label, a value (null when there is nothing to measure) and a context line.
 */
export function profileFigures(profile, now = new Date()) {
    const workplaces = profile.workplaces ?? [];
    const certifications = profile.certifications ?? [];
    const currentRoles = workplaces.filter(workplace => !workplace.endedAt).length;
    const currentCertifications = certifications.filter(certification => isCurrentCertification(certification, now)).length;
    const skillCount = distinctTagCount(profile);
    return [
        experienceFigure(workplaces, currentRoles > 0, now),
        { context: workplaces.length > 0 ? `${currentRoles} current` : 'No roles yet', label: 'Roles', value: workplaces.length },
        { context: certifications.length > 0 ? `${currentCertifications} current` : 'None yet', label: 'Certifications', value: certifications.length },
        { context: skillCount > 0 ? 'Across every section' : 'None yet', label: 'Skills', value: skillCount }
    ];
}

function experienceFigure(workplaces, hasCurrentRole, now) {
    if (workplaces.length === 0) return { context: 'No roles yet', label: 'Experience', value: null };
    const startMonths = workplaces.map(workplace => monthIndex(workplace.startedAt));
    const earliestStart = Math.min(...startMonths);
    const latestEnd = hasCurrentRole
        ? monthIndex(now)
        : Math.max(...workplaces.map(workplace => monthIndex(workplace.endedAt)));
    return {
        context: `Since ${sectionMonthAndYear(workplaces[startMonths.indexOf(earliestStart)].startedAt)}`,
        label: 'Experience',
        value: spanLabel(latestEnd - earliestStart)
    };
}

function monthIndex(date) {
    if (date instanceof Date) return date.getUTCFullYear() * MONTHS_IN_YEAR + date.getUTCMonth();
    const [ year, month = 1 ] = date.split('-').map(Number);
    return year * MONTHS_IN_YEAR + month - 1;
}

function spanLabel(months) {
    const years = Math.floor(months / MONTHS_IN_YEAR);
    if (years >= 1) return `${years} ${years === 1 ? 'year' : 'years'}`;
    if (months >= 1) return `${months} ${months === 1 ? 'month' : 'months'}`;
    return 'Under a month';
}

function isCurrentCertification(certification, now) {
    return !certification.expiresAt || monthIndex(certification.expiresAt) >= monthIndex(now);
}

function distinctTagCount(profile) {
    const sections = [ 'workplaces', 'schools', 'certifications', 'projects', 'repositories' ]
        .flatMap(sectionKey => profile[sectionKey] ?? []);
    const labels = sections.flatMap(section => (section.tags ?? []).map(tag => tag.label.toLowerCase()));
    return new Set(labels).size;
}

/**
 * Labels a profile section by how many entries it holds, naming an empty section plainly rather than with a zero.
 *
 * @param {number} count - The number of entries in the section.
 * @param {string} singular - The noun for one entry.
 * @param {string} [plural] - The noun for several entries, when it is not the singular with an s.
 * @returns {string} - The label, such as "5 roles" or "No roles yet".
 */
export function sectionCountLabel(count, singular, plural = `${singular}s`) {
    if (count === 0) return `No ${plural} yet`;
    return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Formats the span of a dated profile section, with an open end reading as the present.
 *
 * @param {string|Date} startedAt - When the section started.
 * @param {string|Date|null} [endedAt] - When it ended, or nothing while it continues.
 * @returns {string} - The span, such as "July 2022 → Present".
 */
export function sectionPeriod(startedAt, endedAt) {
    return `${sectionMonthAndYear(startedAt)} → ${endedAt ? sectionMonthAndYear(endedAt) : 'Present'}`;
}

export function sectionMonthAndYear(date) {
    if (date instanceof Date) {
        const monthName = date.toLocaleString('default', { month: 'long' }),
            year = date.getUTCFullYear();
        return `${monthName} ${year}`;
    }
    const [ year, month ] = date.split('-');
    if (!month) return year;
    const monthIndex = month - 1,
        monthName = new Date(year, monthIndex, 1).toLocaleString('default', { month: 'long' });
    return [ monthName, year ].join(' ');
}

export function sectionYear(date) {
    if (date instanceof Date) {
        return date.getUTCFullYear();
    }
    const [ year ] = date.split('-');
    return year;
}

export function sortAllProfileLinks(profileLinks = []) {
    return profileLinks.sort((a, b) => {
        const aDeleted = a.deletedAt !== null ? 1 : 0;
        const bDeleted = b.deletedAt !== null ? 1 : 0;
        if (aDeleted !== bDeleted) {
            return aDeleted - bDeleted;
        }
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB - dateA;
    });
}

export function sortAllProfileSections(profile = {}) {
    for (const sectionKey of [ 'certifications', 'projects', 'repositories', 'schools', 'workplaces' ]) {
        const sections = profile[sectionKey] ?? [];
        sortProfileSections(sectionKey, sections);
    }
}

export function sortProfileSections(sectionKey, sections = []) {
    if (!sectionSortKeys.has(sectionKey)) return;
    const sortKeys = sectionSortKeys.get(sectionKey);
    return sortProfileSectionsByKeys(sections, sortKeys);
}

function sortProfileSectionsByKeys(sections, sortKeys) {
    return sections.sort((a, b) => {
        for (const [ key, order = 'asc', type = null ] of sortKeys) {
            let compareResult;
            if (type === Date) {
                const aDate = a[key]?.replaceAll('-', '') ?? Infinity;
                const bDate = b[key]?.replaceAll('-', '') ?? Infinity;
                compareResult = order.toLowerCase() === 'desc' ? bDate - aDate : aDate - bDate;
            }
            else {
                compareResult = order.toLowerCase() === 'desc' ? b[key] < a[key] ? -1 : 1 : a[key] < b[key] ? -1 : 1;
            }
            if (compareResult !== 0) {
                return compareResult;
            }
        }
        return 0;
    });
}

export function sortAllProfileSectionTags(profile = {}) {
    for (const sectionKey of [ 'certifications', 'projects', 'repositories', 'schools', 'workplaces' ]) {
        const sections = profile[sectionKey] || [];
        for (const section of sections) {
            sortProfileSectionTags(section);
        }
    }
}

export function sortProfileSectionTags(section = {}) {
    if (!section.tags) {
        return;
    }
    sortTags(section.tags);
}

export function sortTags(tags) {
    return tags.sort((a, b) => {
        if (a.label < b.label) return -1;
        if (a.label > b.label) return 1;
        return 0;
    });
}

export function userProfileHasAttribute(userProfile, attribute) {
    return userProfile?.attributes && userProfile.attributes.length > 0
        ? userProfile.attributes.includes(attribute)
        : false;
}
